import axios from "axios";
import {
  getStoredToken,
  isTokenExpired,
  clearAuthAndRedirectToLogin,
} from "../utils/auth";
import { getApiBaseUrl } from "../config/apiBase";

const API_BASE = getApiBaseUrl();

const livechatAPI = axios.create({
  baseURL: `${API_BASE}/api/livechat`,
  headers: { "Content-Type": "application/json" },
});

livechatAPI.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (!token || isTokenExpired(token)) {
    clearAuthAndRedirectToLogin();
    return Promise.reject(new Error("Token expired"));
  }
  config.headers.Authorization = `Bearer ${token}`;
  return config;
});

livechatAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearAuthAndRedirectToLogin();
    }
    return Promise.reject(error);
  }
);

/**
 * Get user's livechat access level from token
 * @returns {{ role: string, sections: string[], accessLevel: 'all' | 'own' | null }}
 */
export const getLivechatAccessInfo = () => {
  const token = localStorage.getItem("token");
  if (!token) return { role: null, sections: [], accessLevel: null };
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const role = payload.role;
    const sections = payload.sections || [];
    
    let accessLevel = null;
    if (role === 'super_admin') {
      accessLevel = 'all';
    } else if (sections.includes('livechats.all')) {
      accessLevel = 'all';
    } else if (sections.includes('livechats.own')) {
      accessLevel = 'own';
    }
    
    return {
      role,
      sections,
      accessLevel,
      userId: payload.user_id,
      platformAccountId: payload.platform_account_id,
    };
  } catch (e) {
    console.error("Error decoding token:", e);
    return { role: null, sections: [], accessLevel: null };
  }
};

const LIVE_CHAT_PAGE_SIZE = 50;

/**
 * Get all conversations based on user's access level (paginated)
 * @param {string|null} phoneNumberId - Optional phone number ID filter
 * @param {Object} options - { limit, page, agent_ids, status_ids, date_from, date_to, window_state }
 */
export const getConversations = async (phoneNumberId = null, options = {}) => {
  const {
    limit = LIVE_CHAT_PAGE_SIZE,
    page = 1,
    agent_ids,
    status_ids,
    date_from,
    date_to,
    window_state,
  } = options;
  const params = { limit, page };
  if (phoneNumberId) params.phone_number_id = phoneNumberId;
  if (agent_ids && agent_ids.length) params.agent_ids = agent_ids.join(",");
  if (status_ids && status_ids.length) params.status_ids = status_ids.join(",");
  if (date_from) params.date_from = date_from;
  if (date_to) params.date_to = date_to;
  if (window_state) params.window_state = window_state;
  return livechatAPI.get("/conversations", { params });
};

/**
 * Get single conversation details
 * @param {string} customerPhone - Customer phone number
 * @param {string} phoneNumberId - Phone number ID
 */
export const getConversation = async (customerPhone, phoneNumberId) => {
  return livechatAPI.get("/conversation", {
    params: {
      customer_phone: customerPhone,
      phone_number_id: phoneNumberId,
    },
  });
};

/**
 * Get messages for a conversation
 * @param {string} customerPhone - Customer phone number
 * @param {string} phoneNumberId - Phone number ID
 * @param {number} limit - Number of messages
 * @param {number} offset - Offset for pagination
 */
export const getMessages = async (customerPhone, phoneNumberId, limit = 50, offset = 0) => {
  if (!customerPhone || !phoneNumberId) {
    throw new Error("customerPhone and phoneNumberId are required");
  }
  return livechatAPI.get("/messages", {
    params: {
      customer_phone: String(customerPhone),
      phone_number_id: String(phoneNumberId),
      limit,
      offset,
    },
  });
};

/**
 * Check if conversation window is open (24-hour rule)
 */
export const checkWindowStatus = async (customerPhone, phoneNumberId) => {
  return livechatAPI.get("/window-status", {
    params: {
      customer_phone: customerPhone,
      phone_number_id: phoneNumberId,
    },
  });
};

/**
 * Mark messages as read
 * @param {string} customerPhone - Customer phone number
 * @param {string} phoneNumberId - Phone number ID
 */
export const markAsRead = async (customerPhone, phoneNumberId) => {
  return livechatAPI.post("/mark-read", {
    customer_phone: customerPhone,
    phone_number_id: phoneNumberId,
  });
};

/**
 * Assign or reassign chat to an agent (super_admin only)
 * @param {string} customerPhone - Customer phone number
 * @param {string} phoneNumberId - Phone number ID
 * @param {number|null} agentId - Agent ID (null to unassign)
 */
export const assignChat = async (customerPhone, phoneNumberId, agentId) => {
  return livechatAPI.post("/assign", {
    customer_phone: customerPhone,
    phone_number_id: phoneNumberId,
    agent_id: agentId,
  });
};

/**
 * Get available agents for assignment (super_admin only)
 */
export const getAvailableAgents = async () => {
  return livechatAPI.get("/available-agents");
};

// ─── Chat statuses ─────────────────────────────────────────────────────────

/**
 * Get all chat statuses for the account (for dropdown and filters)
 */
export const getStatuses = async () => {
  return livechatAPI.get("/statuses");
};

/**
 * Create a chat status (super_admin only)
 * @param {{ name: string, color?: string, display_order?: number }}
 */
export const createStatus = async (payload) => {
  return livechatAPI.post("/statuses", payload);
};

/**
 * Update a chat status (super_admin only)
 */
export const updateStatus = async (id, payload) => {
  return livechatAPI.put(`/statuses/${id}`, payload);
};

/**
 * Delete a chat status (super_admin only)
 */
export const deleteStatus = async (id) => {
  return livechatAPI.delete(`/statuses/${id}`);
};

/**
 * Update conversation status
 * @param {string} customerPhone
 * @param {string} phoneNumberId
 * @param {number|null} statusId
 */
export const updateConversationStatus = async (customerPhone, phoneNumberId, statusId) => {
  return livechatAPI.patch("/conversation/status", {
    customer_phone: customerPhone,
    phone_number_id: phoneNumberId,
    status_id: statusId,
  });
};

/**
 * Get agent analysis (super_admin only)
 * @param {{ agent_ids?: number[], status_ids?: number[], date_from?: string, date_to?: string, phone_id?: string }}
 */
export const getAgentAnalysis = async (filters = {}) => {
  const params = {};
  if (filters.agent_ids?.length) params.agent_ids = filters.agent_ids.join(",");
  if (filters.status_ids?.length) params.status_ids = filters.status_ids.join(",");
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  if (filters.phone_id) params.phone_id = filters.phone_id;
  return livechatAPI.get("/agent-analysis", { params });
};

export default livechatAPI;
